/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TPreview } from "@tivocorp/data-domain-lib";

import { DateTime } from "../../model/utility/DateTime";
import { Locale, LocaleKeys } from "../../model/locale";
import { resolveInternalRatingNames } from "./internalRatings";

/**
 * PreviewFormatter — Utility class to derive UI-ready formatted strings
 * from an `TPreview` (any preview api response).
 */
export class PreviewFormatter {
  private mPreview: TPreview | undefined;

  public constructor(preview: TPreview | undefined) {
    this.mPreview = preview;
  }

  /**
   * @returns true when the value is a non-null, non-undefined.
   */
  private isValid = <T>(value: T | null | undefined): value is T => {
    return value !== null && value !== undefined;
  };

  /**
   * @returns true when the value is a non-null, non-undefined, non-empty string.
   */
  private isValidString(value: string | null | undefined): boolean {
    return this.isValid(value) && value.length > 0;
  }

  /**
   * @returns value from of a key from a object if present otherwise undefined.
   */
  private readProperty<T, K extends keyof T>(
    obj: T | null | undefined,
    key: K
  ): T[K] | undefined {
    return this.isValid<T>(obj) ? (obj as T)[key] : undefined;
  }

  /**
   * @returns true when the preview is a movie, false otherwise.
   */
  public isMoviePreview(): boolean {
    const contentType = this.readProperty(this.mPreview, "contentType") || "";
    const previewType = this.readProperty(this.mPreview, "previewType") || "";
    return contentType.includes("movie") || previewType.includes("movie");
  }

  /**
   * @returns title string from the preview, or an empty string when not present.
   */
  public getTitle(): string {
    return this.readProperty(this.mPreview, "title") || "";
  }

  /**
   * @returns decorated episode title in format "S1 : E5 – Episode Title".
   */
  public getDecoratedEpisodeTitle(): string {
    const seasonNumber = this.readProperty(this.mPreview, "seasonNumber");
    const episodeNumber = this.readProperty(this.mPreview, "episodeNumber");
    const episodeTitle = this.readProperty(this.mPreview, "episodeTitle");

    let decoratedTitle: string = "";
    if (seasonNumber !== undefined) decoratedTitle += "S" + seasonNumber;
    if (episodeNumber !== undefined) decoratedTitle += " : E" + episodeNumber;
    if (decoratedTitle.length > 0) decoratedTitle += " - ";
    decoratedTitle += episodeTitle || "";

    return decoratedTitle;
  }

  /**
   * @returns the description string from the preview or DESCRIPTION_NOT_AVAILABLE when not present.
   */
  public getDescription(): string {
    return (
      this.readProperty(this.mPreview, "description") ||
      Locale.getString(LocaleKeys.DESCRIPTION_NOT_AVAILABLE)
    );
  }

  /**
   * @returns a list of display name strings for internal ratings, resolved via
   * PreviewInteractor. Falls back to an empty array when no ratings are present
   * or no display name can be resolved.
   */
  public getInternalRatings(): string[] {
    return resolveInternalRatingNames(
      this.readProperty(this.mPreview, "internalRatingList")
    );
  }

  /**
   * @returns the star rating value when available. Otherwise returns null.
   */
  public getStarRating(): number | null {
    const starRating = this.readProperty(this.mPreview, "starRating");
    if (starRating !== undefined) return starRating;
    return null;
  }

  /**
   * @returns a category string from the preview, or an empty string when not present.
   */
  public getCategoryLabels(): string {
    return this.readProperty(this.mPreview, "categoryLabel") || "";
  }

  /**
   * @param locale - Optional locale string for date formatting (default: "en").
   * @returns the program start date formatted as "Wed 10/25" using DateTime,
   * or an empty string when startTime is absent or invalid.
   */
  public getFormattedStartDate(locale: string = "en"): string {
    const startTime = this.readProperty(this.mPreview, "startTime");
    if (startTime === undefined) return "";

    const formattedStartDate = new DateTime(startTime).toLocaleDayWithDate(
      locale
    );
    return formattedStartDate;
  }

  /**
   * @returns the time range formatted as "9:30PM - 10:30PM" using DateTime,
   * or an empty string when either startTime or endTime is absent or invalid.
   */
  public getFormattedTimeRange(): string {
    const startTime = this.readProperty(this.mPreview, "startTime");
    const endTime = this.readProperty(this.mPreview, "endTime");
    if (startTime === undefined || endTime === undefined) return "";

    const start: DateTime = new DateTime(startTime);
    const end: DateTime = new DateTime(endTime);
    const timeRange = start.to12HourClock() + " - " + end.to12HourClock();

    return timeRange;
  }

  /**
   * @returns the first-aired label, e.g. "(First aired/First airs) 10/25/2023" using DateTime,
   * or an empty string when firstAiredDate is absent or invalid.
   */
  public getFirstAiredText(): string {
    const firstAired = this.readProperty(this.mPreview, "firstAiredDate");
    if (firstAired === undefined) return "";

    const firstAiredDate: DateTime = new DateTime(firstAired);
    const currentTime = new DateTime();
    const isFutureAiring = firstAiredDate.getTime() > currentTime.getTime();

    const prefixText: string = isFutureAiring
      ? Locale.getString(LocaleKeys.FIRST_AIRS)
      : Locale.getString(LocaleKeys.FIRST_AIRED);

    const formattedFirstAired = firstAiredDate.strftime(
      `${prefixText} %M/%D/%y`
    );

    return formattedFirstAired;
  }

  /**
   * @returns the duration in minutes formatted as "60m" else empty string when invalid.
   */
  public getDurationMinutes(): string {
    const duration = this.readProperty(this.mPreview, "duration");
    if (duration === undefined) return "";
    const durationInMinutes = duration / 60;
    return `${durationInMinutes}m`;
  }

  /**
   * @returns Movie release year from the preview, or undefined when not present or not a movie.
   */
  public getMovieReleaseYear(): string {
    if (this.isMoviePreview() === false) return "";
    const movieYear = this.readProperty(this.mPreview, "movieYear");
    return `${Locale.getString(LocaleKeys.GUIDE_PREVIEW_RELEASE)} ${movieYear}`;
  }

  /**
   * @returns the video resolution string from the preview, or an empty string when not present.
   */
  public getVideoResolution(): string {
    return this.readProperty(this.mPreview, "videoResolution") || "";
  }

  /**
   * @returns whether program has closed captions
   */
  public hasCC(): boolean {
    return this.readProperty(this.mPreview, "hasCc") || false;
  }

  /**
   * @returns whether program isNew
   */
  public isNew(): boolean {
    return this.readProperty(this.mPreview, "isNew") || false;
  }
}
